<template>
  <nav class="site-nav" :class="{ 'site-nav--scrolled': isScrolled }">
    <div class="site-nav__inner">
      <!-- Logo -->
      <router-link to="/" class="site-nav__logo">Ravindra Bachate</router-link>

      <!-- Desktop links -->
      <div class="site-nav__links">
        <router-link
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          class="site-nav__link"
          :class="{ 'site-nav__link--contact': item.name === 'Contact' }"
          exact-active-class="site-nav__link--active"
        >{{ item.name }}</router-link>
      </div>

      <!-- Dark mode toggle -->
      <button class="site-nav__theme-btn" @click="toggleDark" :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'">
        <svg v-if="isDark" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
      </button>

      <!-- Mobile hamburger -->
      <button class="site-nav__hamburger" @click="menuOpen = !menuOpen" aria-label="Toggle menu">
        <span v-if="!menuOpen">&#9776;</span>
        <span v-else>&times;</span>
      </button>
    </div>

    <!-- Mobile dropdown -->
    <transition name="mobile-menu">
      <div v-if="menuOpen" class="site-nav__mobile">
        <router-link
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          class="site-nav__mobile-link"
          exact-active-class="site-nav__mobile-link--active"
          @click="menuOpen = false"
        >{{ item.name }}</router-link>
      </div>
    </transition>
  </nav>
</template>

<script>
export default {
  name: 'FloatingHeader',
  data() {
    return {
      isScrolled: false,
      menuOpen: false,
      isDark: false,
      navItems: [
        { name: 'Home',       path: '/' },
        { name: 'Work',       path: '/workpage' },
        { name: 'Research',   path: '/researchpage' },
        { name: 'Mentorship', path: '/mentorshippage' },
        { name: 'Blog',       path: '/blogpage' },
        { name: 'Contact',    path: '/contactpage' },
      ],
    };
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll);
    // Initialise dark mode from localStorage, or system preference
    const stored = localStorage.getItem('theme');
    if (stored) {
      this.isDark = stored === 'dark';
    } else {
      this.isDark = false;
    }
    this.applyTheme();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    onScroll() {
      this.isScrolled = window.scrollY > 20;
    },
    toggleDark() {
      this.isDark = !this.isDark;
      localStorage.setItem('theme', this.isDark ? 'dark' : 'light');
      this.applyTheme();
    },
    applyTheme() {
      document.documentElement.setAttribute('data-theme', this.isDark ? 'dark' : 'light');
    },
  },
};
</script>

<style scoped>
.site-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: var(--color-bg-white);
  border-bottom: 1px solid var(--color-border);
  transition: box-shadow var(--duration-slow) var(--ease-out);
}
.site-nav--scrolled {
  box-shadow: var(--shadow-md);
}

.site-nav__inner {
  max-width: var(--container-xl);
  margin: 0 auto;
  height: var(--nav-height);
  padding: 0 var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.site-nav__logo {
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  text-decoration: none;
  transition: color var(--duration-base) var(--ease-out);
  white-space: nowrap;
}
.site-nav__logo:hover { color: var(--color-primary); }

/* Desktop links */
.site-nav__links {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.site-nav__link {
  padding: 0.45rem 1rem;
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--text-xl);
  font-weight: var(--font-normal);
  color: var(--color-text-700);
  text-decoration: none;
  transition: background var(--duration-base) var(--ease-out), color var(--duration-base) var(--ease-out);
}
.site-nav__link:hover,
.site-nav__link--active {
  background: var(--color-primary-subtle);
  color: var(--color-primary);
}
/* Contact CTA pill
.site-nav__link--contact {
  border: 1.5px solid var(--color-primary);
  color: var(--color-primary);
  border-radius: var(--radius-full);
  padding: 0.4rem 1.1rem;
}*/
.site-nav__link--contact:hover,
.site-nav__link--contact.site-nav__link--active {
  background: var(--color-primary);
  color: var(--color-bg-white);
}

/* Dark mode toggle */
.site-nav__theme-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: var(--radius-full);
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border);
  color: var(--color-text-700);
  cursor: pointer;
  transition: background var(--duration-base) var(--ease-out), color var(--duration-base) var(--ease-out), border-color var(--duration-base) var(--ease-out);
  flex-shrink: 0;
}
.site-nav__theme-btn:hover {
  background: var(--color-primary-subtle);
  border-color: var(--color-primary-border);
  color: var(--color-primary);
}

/* Hamburger */
.site-nav__hamburger {
  display: none;
  background: none;
  border: none;
  font-size: var(--text-xl);
  cursor: pointer;
  color: var(--color-text-700);
  padding: 0.25rem var(--space-2);
  line-height: 1;
}

/* Mobile dropdown */
.site-nav__mobile {
  background: var(--color-bg-white);
  border-top: 1px solid var(--color-border);
  padding: 0.75rem var(--space-6);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.site-nav__mobile-link {
  display: block;
  padding: 0.7rem 1rem;
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--text-base);
  font-weight: var(--font-normal);
  color: var(--color-text-700);
  text-decoration: none;
  transition: background var(--duration-base) var(--ease-out), color var(--duration-base) var(--ease-out);
}
.site-nav__mobile-link:hover,
.site-nav__mobile-link--active {
  background: var(--color-primary-subtle);
  color: var(--color-primary);
}
.mobile-menu-enter-active,
.mobile-menu-leave-active { transition: opacity var(--duration-base) var(--ease-out), transform var(--duration-base) var(--ease-out); }
.mobile-menu-enter-from,
.mobile-menu-leave-to { opacity: 0; transform: translateY(-8px); }

/* Responsive */
@media (max-width: 768px) {
  .site-nav__links { display: none; }
  .site-nav__hamburger { display: block; }
}
/* Theme button always visible on all screen sizes — already block/flex */
</style>
